import { useRef, useState } from 'react';
import { useSelector } from 'react-redux';

import { landingApi } from '../../api';
import { EFillApplicationStickyButtonType } from '../../containers/stickyButtons/FillApplicationButton/types';
import {
  openApplicationStatusModal,
  openUnfinishedAnketaModal,
  setAuthorizedModal,
  setModalOpen,
  setTermsModalOpen,
} from '../../redux/landingSlice';
import { selectFinancePlatformEnabled, selectShortFormGroup } from '../../selectors/landing';
import { selectIsAuthorized, selectIsCianAuthorized } from '../../selectors/user';
import { ERuntimeSettings } from '../../types/runtimeSettings';
import { useApplicationContext } from '../../utils/applicationContext';
import { buildDraftActionUrl } from '../../utils/buildDraftActionUrl';
import { resolveActualApplication } from '../../utils/resolveActualApplication';
import {
  ETargetElementForAnalytics,
  TRedirectForAnalytics,
  TTargetElementForAnalytics,
} from '../../utils/tracking/types';
import { useTracking } from '../../utils/tracking/useTracking';
import { CTA_REQUEST_TIMEOUT_MS, withRequestTimeout } from '../../utils/withRequestTimeout';
import { useAppDispatch } from '../common/useAppDispatch';

export interface IFillApplicationActionProps extends IFillApplicationTrackingProps {
  path: string;
  authorizationRequired?: boolean;
}

interface IFillApplicationTrackingProps {
  bankName?: string | null;
  place?: number;
  isMultiOffer?: boolean;
  analyticEventKey: TRedirectForAnalytics;
  targetElementForAnalytics?: TTargetElementForAnalytics;
  text?: string;
}

export const useFillApplicationAction = () => {
  const [isPending, setIsPending] = useState(false);
  /** Повторный вход блокируется ref-ом, а не isPending: два клика в одном тике прочитали бы одно
      и то же старое значение стейта, а ref виден сразу. */
  const inFlightRef = useRef(false);
  const dispatch = useAppDispatch();
  const isCianAuthorized = useSelector(selectIsCianAuthorized);
  const isAuthorized = useSelector(selectIsAuthorized);
  const financePlatformEnabled = useSelector(selectFinancePlatformEnabled);
  const { config } = useApplicationContext();
  const mortgageLandingRedesignEnabled = config.get<boolean>(ERuntimeSettings.mortgageLandingRedesign);
  const mpAuthWidgetEnabled = config.get<boolean>(ERuntimeSettings.mpAuthWidgetEnabled);
  const isShortFormRedirect = useSelector(selectShortFormGroup) === 'B' && Boolean(mortgageLandingRedesignEnabled);
  const { trackRedirect, trackClickOnMasterButton, trackClickOnModalFillApplicationButton } = useTracking();

  const trackClick = ({ bankName, place, isMultiOffer, analyticEventKey }: IFillApplicationTrackingProps) => {
    const isMasterButton =
      analyticEventKey === EFillApplicationStickyButtonType.BOTTOM ||
      analyticEventKey === EFillApplicationStickyButtonType.TOP;

    if (isMasterButton) {
      trackClickOnMasterButton(analyticEventKey);
    } else {
      trackClickOnModalFillApplicationButton({
        place: Number(place),
        bank: bankName as string,
        isMultiOffer: Boolean(isMultiOffer),
        type: analyticEventKey,
      });
    }
  };

  const fillApplicationAction = async ({
    path,
    bankName,
    place,
    analyticEventKey,
    isMultiOffer,
    authorizationRequired = true,
    targetElementForAnalytics = ETargetElementForAnalytics.BUTTON,
  }: IFillApplicationActionProps) => {
    if (inFlightRef.current) {
      return;
    }

    trackClick({ bankName, place, isMultiOffer, analyticEventKey });

    const redirect = (target: string = path) => {
      trackRedirect(analyticEventKey, targetElementForAnalytics, bankName);
      window.open(target, '_self');
    };

    /** Проверяем авторизацию маркетплейса только при включённом виджете. Короткая анкета сама
        логинит пользователя, остальные точки входа требуют логин до редиректа. */
    if (
      mpAuthWidgetEnabled &&
      !isShortFormRedirect &&
      authorizationRequired &&
      !financePlatformEnabled &&
      !isAuthorized
    ) {
      dispatch(
        setAuthorizedModal({
          bankName: bankName as string,
          isOpen: true,
          pathForRedirect: path,
          typeForRedirect: analyticEventKey,
          targetElementForRedirect: targetElementForAnalytics,
        }),
      );

      return;
    }

    if (!isCianAuthorized || !isShortFormRedirect) {
      redirect();

      return;
    }

    inFlightRef.current = true;
    setIsPending(true);

    /** initiate резолвится объектом результата, а не телом ответа: не-200 приходит как { error }
        без data. subscribe: false не оставляет висящей подписки, forceRefetch — чтобы каждый клик
        проверял черновик заново, а не переиспользовал первый ответ до конца жизни страницы. */
    const request = dispatch(
      landingApi.endpoints.getActualApplication.initiate(undefined, { subscribe: false, forceRefetch: true }),
    );
    const response = await withRequestTimeout(request, CTA_REQUEST_TIMEOUT_MS);

    inFlightRef.current = false;
    setIsPending(false);

    const application = response?.data ?? null;
    const status = resolveActualApplication(application);
    const clickedPlace = place ?? null;

    /** Обе модалки флоу гасят чужие перед показом: guard один на оба случая, а иначе новая
        легла бы поверх открытой модалки условий или инфо о кредите. */
    const closeOtherModals = () => {
      dispatch(setTermsModalOpen(false));
      dispatch(setModalOpen(false));
    };

    if (status === 'draft') {
      closeOtherModals();
      dispatch(openUnfinishedAnketaModal({ path, place: clickedPlace }));

      return;
    }

    if (status === 'submitted' && application) {
      closeOtherModals();
      dispatch(openApplicationStatusModal({ application, place: clickedPlace }));

      return;
    }

    /** Ручка ответила, актуальной анкеты нет — уводим с явным draftAction=new, иначе форма может
        подхватить прошлое состояние анкеты. На таймауте и отказе параметр не ставим: черновик мы не
        проверили, а new его сбрасывает. */
    redirect(response?.isSuccess ? buildDraftActionUrl(path, 'new') : path);
  };

  return { isPending, fillApplicationAction };
};
